import { Navigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { PageHeader } from '@platform/ui-kit';
import type { PortalModule } from '../../api';
import { CompanyProfileTab } from './CompanyProfileTab';
import { LicenseUsageTab } from './LicenseUsageTab';
import { SsoTab } from './SsoTab';
import { NotificationsTab } from './NotificationsTab';
import { OpenAiIntegrationTab } from './OpenAiIntegrationTab';
import { AuditLogTab } from './AuditLogTab';
import { SHARED_SECTIONS } from '../shared/sharedSections';

/**
 * Administration & Configuration.
 *
 * Specs: docs/architecture/administration-and-configuration.md (what it does) and
 * navigation-and-breadcrumbs.md (how it is navigated).
 *
 * The sections are the drawer's sections — there are deliberately no tabs here. A
 * tab strip would be a second navigation model competing with the drawer, and the
 * two would disagree about where the user is.
 */
const SECTIONS: Record<string, { title: string; description: string; render: () => JSX.Element }> = {
  'company-profile': {
    title: 'Company Profile',
    description: "This install's client company. Drives portal branding and SSO email matching.",
    render: () => <CompanyProfileTab />,
  },
  'sso-identity': {
    title: 'SSO / Identity',
    description: "Authentication against the client's identity provider.",
    render: () => <SsoTab />,
  },
  notifications: {
    title: 'Notifications',
    description: 'Email and in-app notification settings for this install.',
    render: () => <NotificationsTab />,
  },
  // Was `integrations` / 'Integrations', an empty state with no storage behind it.
  // Renamed on the owner's instruction (2026-10-07) — his wording, 'Open AI
  // Integration', TWO WORDS ("I believe it is called Open AI and not OpenAI"). The key
  // follows the catalogue label through `keyOf`, so the route is now
  // /admin/open-ai-integration.
  'open-ai-integration': {
    title: 'Open AI Integration',
    description: 'Which AI provider each module runs on, and the credentials for each provider.',
    render: () => <OpenAiIntegrationTab />,
  },
  'license-usage': {
    title: 'License Usage',
    description: 'What this install is licensed for, and how much of it is in use.',
    render: () => <LicenseUsageTab />,
  },
  // Identical to the Talogent copies — same definition, not duplicates.
  ...SHARED_SECTIONS,
  'audit-log': {
    title: 'Audit Log',
    description: 'Every administrative change made in this install.',
    render: () => <AuditLogTab />,
  },
};

export interface AdminModuleProps {
  modules: PortalModule[];
}

export function AdminModule({ modules }: AdminModuleProps) {
  const { sectionKey } = useParams<{ sectionKey: string }>();

  // Same guard as Licensing & Usage: `modules` holds only what this user may open,
  // sections included. Anything else is a typed URL and gets redirected, rather than
  // rendering a screen whose API calls will be refused.
  const module = modules.find((m) => m.id === 'admin');
  if (!module || module.sections.length === 0) return <Navigate to="/" replace />;

  const first = module.sections[0]!.path;

  // Module home redirects to the first section, matching every other module.
  if (!sectionKey) return <Navigate to={first} replace />;

  const permitted = module.sections.some((s) => s.key === sectionKey);
  const section = permitted ? SECTIONS[sectionKey] : undefined;
  if (!section) return <Navigate to={first} replace />;

  return (
    <Box>
      <PageHeader title={section.title} description={section.description} />
      {section.render()}
    </Box>
  );
}
